import { Component, EventEmitter, inject, Input, OnChanges, OnInit, Output, Signal, signal, SimpleChanges } from '@angular/core';
import { ProductListItemDTO } from '@iota/inh-core-v1-management-client';
import { EmployeeDTO } from '@iota/iota-management-portal-v4-client';
import { Store } from '@ngrx/store';
import { map, Observable, take } from 'rxjs';
import { LoadCompany } from 'src/app/store/actions/company.actions';
import { companyById } from 'src/app/store/reducer/company.reducer';
import { NaceCodesRev2Descriptions } from '../../constants-and-enums/nace-code';
import { companyRolesToChips, parseMainAddress, RoleChip } from '../../utils';
import { Clipboard } from '@angular/cdk/clipboard';
import { employeesByCompany } from '../../../store/reducer/employee.reducer';
import { CompanyMode } from '../../constants-and-enums/company-mode.enum';
import { CompanyEntity } from '../../../store/entity-models';
import { ProductListService } from '../../services/product-list.service';

@Component({
    standalone: false,
    selector: 'app-company-card',
    templateUrl: './company-card.component.html',
    styleUrls: ['./company-card.component.scss']
})
export class CompanyCardComponent implements OnInit, OnChanges {
    private readonly productListService = inject(ProductListService);

    @Input() companyId: string;
    @Input() displayAdditionalInfo = false;
    @Input() enableEditLogo: boolean;
    @Output() cardClicked = new EventEmitter<void>();
    @Input() companyMode: CompanyMode;

    company$: Observable<CompanyEntity>;
    roleChips$: Observable<RoleChip[]>;
    address$: Observable<string>;
    naceCodeDescription$: Observable<string>;
    showTooltip = false;
    showCheckedIcon = false;
    tooltipText = $localize`Copy number`;
    employees$: Observable<EmployeeDTO[]>;
    products: Signal<ProductListItemDTO[]> = signal([]);
    loadingProducts = this.productListService.loading;

    constructor(
        private store: Store,
        private clipboard: Clipboard
    ) {}

    ngOnInit(): void {
        if (this.companyId) {
            this.loadAndSetValues();
        }
    }

    showCompanyNumberToolTip() {
        this.showTooltip = true;
    }

    hideTooltip() {
        this.showTooltip = false;
    }

    loadAndSetValues() {
        this.store
            .select(companyById(this.companyId))
            .pipe(take(1))
            .subscribe((company) => {
                if (!company) {
                    this.store.dispatch(LoadCompany({ companyId: this.companyId }));
                }
            });

        this.company$ = this.store.select(companyById(this.companyId));
        this.roleChips$ = this.company$.pipe(map((c) => companyRolesToChips(c?.roles)));
        this.address$ = this.company$.pipe(map((c) => parseMainAddress(c.addresses)));
        this.naceCodeDescription$ = this.company$.pipe(map((c) => NaceCodesRev2Descriptions.get(c?.naceCode)));
        this.employees$ = this.store.select(employeesByCompany(this.companyId));
        this.products = this.productListService.byVendorId(this.companyId);

        if (this.companyMode === CompanyMode.PARTNER) {
            this.productListService.load();
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        const newCompanyId = changes?.['companyId']?.currentValue;

        if (newCompanyId) {
            this.loadAndSetValues();
        }
    }

    copyCompanyNumber(company: CompanyEntity) {
        this.clipboard.copy(company.companyNumber);
        this.tooltipText = $localize`Copied`;
        this.showCheckedIcon = true;
        this.showTooltip = true;

        setTimeout(() => {
            this.tooltipText = $localize`Copy number`;
            this.showCheckedIcon = false;
            this.showTooltip = false;
        }, 2000);
    }

    protected readonly CompanyMode = CompanyMode;
}
